import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { TextInput } from "@/components/ui/Field";
import { htmlToPlainText } from "@/lib/mailHtml";
import {
  encodeLocalMailAttachments,
  formatAttachmentSize,
  localMailAttachmentsFromFileList,
  type LocalMailAttachment,
  type MailAttachmentPayload,
} from "@/lib/mailAttachments";
import { mailSharepointSuggestions, type SharePointMailItem } from "@/lib/orders";
import { setGlobalUploadSuppressed } from "@/features/globalUpload/globalUploadSuppress";
import { SharePointMailAttachmentList } from "@/components/SharePointMailAttachmentList";

export type MailReviewResult =
  | { action: "back" }
  | {
      action: "mailto";
      to: string;
      subject: string;
      bodyHtml: string;
      bodyPlain: string;
      sharepointItemIds: string[];
      attachments: MailAttachmentPayload[];
    }
  | {
      action: "graph";
      to: string;
      subject: string;
      bodyHtml: string;
      bodyPlain: string;
      sharepointItemIds: string[];
      attachments: MailAttachmentPayload[];
    }
  | {
      action: "copy";
      subject: string;
      bodyHtml: string;
      bodyPlain: string;
      sharepointItemIds: string[];
      attachments: MailAttachmentPayload[];
    };

export function MailReviewDialog({
  open,
  orderIds,
  auftragIds = [],
  initialTo,
  initialSubject,
  initialBodyHtml,
  isEntra,
  onResolve,
}: {
  open: boolean;
  orderIds: number[];
  auftragIds?: number[];
  initialTo: string;
  initialSubject: string;
  initialBodyHtml: string;
  isEntra: boolean;
  onResolve: (result: MailReviewResult) => void;
}) {
  const [to, setTo] = useState(initialTo);
  const [subject, setSubject] = useState(initialSubject);
  const [bodyHtml, setBodyHtml] = useState(initialBodyHtml);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const [spItems, setSpItems] = useState<SharePointMailItem[]>([]);
  const [spHint, setSpHint] = useState("");
  const [spLoading, setSpLoading] = useState(false);
  const [selectedSp, setSelectedSp] = useState<Set<string>>(new Set());
  const [localAttachments, setLocalAttachments] = useState<LocalMailAttachment[]>([]);
  const [localDropActive, setLocalDropActive] = useState(false);
  const [resolving, setResolving] = useState(false);
  const localFileInputRef = useRef<HTMLInputElement | null>(null);
  const localDropCounterRef = useRef(0);

  const syncEditorDom = useCallback((html: string) => {
    if (editorRef.current && editorRef.current.innerHTML !== html) {
      editorRef.current.innerHTML = html;
    }
  }, []);

  const attachEditorRef = useCallback(
    (node: HTMLDivElement | null) => {
      editorRef.current = node;
      if (node && open) {
        syncEditorDom(bodyHtml);
      }
    },
    [open, bodyHtml, syncEditorDom],
  );

  useEffect(() => {
    if (!open) {
      setBodyHtml("");
      return;
    }
    setTo(initialTo);
    setSubject(initialSubject);
    setBodyHtml(initialBodyHtml);
    setSelectedSp(new Set());
    setLocalAttachments([]);
    setLocalDropActive(false);
    localDropCounterRef.current = 0;
  }, [open, initialTo, initialSubject, initialBodyHtml]);

  useEffect(() => {
    if (!open) {
      setGlobalUploadSuppressed(false);
      return;
    }
    setGlobalUploadSuppressed(true);
    return () => setGlobalUploadSuppressed(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    syncEditorDom(bodyHtml);
  }, [open, bodyHtml, syncEditorDom]);

  const loadSharepointItems = useCallback(async () => {
    if (!orderIds.length && !auftragIds.length) {
      setSpItems([]);
      setSpHint("");
      return;
    }
    setSpLoading(true);
    setSpHint("Lade Projektdateien …");
    try {
      const payload = await mailSharepointSuggestions({
        orderIds: orderIds.length ? orderIds : undefined,
        auftragIds: orderIds.length ? undefined : auftragIds,
      });
      const items = Array.isArray(payload?.items) ? payload.items : [];
      setSpItems(items);
      const nProj = payload?.sharepoint_projects ?? 0;
      if (!nProj) setSpHint("Keine SharePoint-Projektordner zu diesen Aufträgen gefunden.");
      else if (!items.length)
        setSpHint("In den verknüpften Projektordnern wurden keine Dateien gefunden.");
      else
        setSpHint(
          `${items.length} Datei(en) aus ${nProj} Projektordner(n): auswählen für Direktversand (Microsoft).`,
        );
    } catch {
      setSpHint("Dateivorschläge konnten nicht geladen werden.");
    } finally {
      setSpLoading(false);
    }
  }, [orderIds, auftragIds]);

  useEffect(() => {
    if (!open) {
      setSpItems([]);
      setSpHint("");
      setSpLoading(false);
      return;
    }
    void loadSharepointItems();
  }, [open, loadSharepointItems]);

  function spIds(): string[] {
    return [...selectedSp];
  }

  function readBodyHtml(): string {
    return editorRef.current?.innerHTML?.trim() ? editorRef.current.innerHTML : bodyHtml;
  }

  function payloadBase() {
    const html = readBodyHtml();
    const plain = htmlToPlainText(html);
    const effectiveSubject = subject.trim() || initialSubject.trim();
    return { bodyHtml: html, bodyPlain: plain, subject: effectiveSubject, sharepointItemIds: spIds() };
  }

  function addLocalFiles(fileList: FileList | File[]) {
    const next = localMailAttachmentsFromFileList(fileList);
    if (!next.length) return;
    setLocalAttachments((prev) => [...prev, ...next]);
  }

  async function resolveWithAction(
    action: Exclude<MailReviewResult["action"], "back">,
    extra?: Partial<MailReviewResult>,
  ) {
    setResolving(true);
    try {
      const attachments = await encodeLocalMailAttachments(localAttachments);
      const base = payloadBase();
      onResolve({ action, ...base, attachments, ...extra } as MailReviewResult);
    } finally {
      setResolving(false);
    }
  }

  const mailPayload = payloadBase();
  const mailReady = mailPayload.subject.length > 0 && mailPayload.bodyPlain.length > 0;
  const graphRecipientReady = to.trim().length > 0;

  return (
    <Dialog
      open={open}
      onClose={() => onResolve({ action: "back" })}
      title="E-Mail-Vorschau"
      size="lg"
      zIndexClass="z-[120]"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={() => onResolve({ action: "back" })}>
            Zurück zur Bestellung
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={!mailReady || resolving}
            onClick={() => void resolveWithAction("copy")}
          >
            Bestellnummer kopieren
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={!mailReady || resolving}
            onClick={() => void resolveWithAction("mailto", { to })}
          >
            Im Mail-Programm öffnen
          </Button>
          <Button
            type="button"
            disabled={!mailReady || !graphRecipientReady || resolving}
            loading={resolving}
            title={!graphRecipientReady ? "Empfänger-E-Mail-Adresse erforderlich" : undefined}
            onClick={() => void resolveWithAction("graph", { to })}
          >
            Direkt senden (Microsoft)
          </Button>
        </>
      }
    >
      {isEntra ? (
        <p className="mb-3 text-xs text-label-secondary">
          Mit Microsoft angemeldet: Direktversand erfolgt über Ihr eigenes Postfach als HTML-Mail.
        </p>
      ) : null}
      <TextInput
        label="An"
        value={to}
        onChange={(e) => setTo(e.target.value)}
        className="mb-2"
        hint={!graphRecipientReady ? "Für Direktversand ist eine Empfänger-Adresse erforderlich." : undefined}
      />
      <TextInput label="Betreff" value={subject} onChange={(e) => setSubject(e.target.value)} className="mb-2" />

      <label className="mb-1 block text-xs font-medium text-label-secondary">Nachricht</label>
      {!mailReady ? (
        <p className="mb-2 text-xs text-amber-700">
          Betreff oder Nachricht fehlen — bitte prüfen oder zur Bestellung zurückkehren.
        </p>
      ) : null}
      <div
        ref={attachEditorRef}
        className="eh-mail-html-editor mb-3 min-h-[280px] w-full text-sm"
        contentEditable
        suppressContentEditableWarning
        onInput={() => {
          if (editorRef.current) setBodyHtml(editorRef.current.innerHTML);
        }}
      />

      {orderIds.length > 0 || auftragIds.length > 0 ? (
        <section className="rounded-ios border border-separator p-3">
          <div className="mb-1 flex items-center justify-between gap-2">
            <p className="text-sm font-semibold text-label">Anhänge aus Projektordner (SharePoint)</p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              title="Anhänge neu laden"
              aria-label="Anhänge neu laden"
              loading={spLoading}
              disabled={spLoading}
              onClick={() => void loadSharepointItems()}
            >
              ↻
            </Button>
          </div>
          <p className="mb-2 text-xs text-label-tertiary">{spHint}</p>
          <SharePointMailAttachmentList
            items={spItems}
            selectedIds={selectedSp}
            onToggle={(id, checked) => {
              setSelectedSp((prev) => {
                const next = new Set(prev);
                if (checked) next.add(id);
                else next.delete(id);
                return next;
              });
            }}
          />
        </section>
      ) : null}

      <section className="mt-3 rounded-ios border border-separator p-3">
        <p className="mb-1 text-sm font-semibold text-label">Eigene Anhänge (vom Computer)</p>
        <p className="mb-2 text-xs text-label-tertiary">
          Dateien hier ablegen oder auswählen — werden beim Direktversand (Microsoft) mitgesendet.
        </p>
        <div
          className={`mb-2 rounded-lg border-2 border-dashed px-4 py-6 text-center text-sm transition-colors ${
            localDropActive
              ? "border-accent bg-accent/10 text-label"
              : "border-separator text-label-secondary"
          }`}
          onDragEnter={(e) => {
            if (!e.dataTransfer?.types?.includes("Files")) return;
            e.preventDefault();
            e.stopPropagation();
            localDropCounterRef.current += 1;
            setLocalDropActive(true);
          }}
          onDragOver={(e) => {
            if (!e.dataTransfer?.types?.includes("Files")) return;
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = "copy";
          }}
          onDragLeave={(e) => {
            if (!e.dataTransfer?.types?.includes("Files")) return;
            e.preventDefault();
            e.stopPropagation();
            localDropCounterRef.current = Math.max(0, localDropCounterRef.current - 1);
            if (localDropCounterRef.current === 0) setLocalDropActive(false);
          }}
          onDrop={(e) => {
            if (!e.dataTransfer?.files?.length) return;
            e.preventDefault();
            e.stopPropagation();
            localDropCounterRef.current = 0;
            setLocalDropActive(false);
            addLocalFiles(e.dataTransfer.files);
          }}
        >
          Dateien hierher ziehen
        </div>
        <input
          ref={localFileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) addLocalFiles(e.target.files);
            e.target.value = "";
          }}
        />
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => localFileInputRef.current?.click()}
        >
          Dateien auswählen …
        </Button>
        {localAttachments.length ? (
          <ul className="mt-3 space-y-1 text-sm">
            {localAttachments.map((entry) => (
              <li
                key={entry.id}
                className="flex items-center justify-between gap-2 rounded-md bg-fill-tertiary px-2 py-1"
              >
                <span className="min-w-0 break-all">
                  {entry.file.name}
                  {entry.file.size ? ` · ${formatAttachmentSize(entry.file.size)}` : ""}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={`${entry.file.name} entfernen`}
                  onClick={() =>
                    setLocalAttachments((prev) => prev.filter((x) => x.id !== entry.id))
                  }
                >
                  ×
                </Button>
              </li>
            ))}
          </ul>
        ) : null}
      </section>
    </Dialog>
  );
}
